import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useOktaSession } from "../okta/OktaSessionContext.jsx";
import { readStoredSswsSession } from "../okta/oktaSessionStorage.js";
import {
  ACTIVE_PROJECT_PROVIDER_EVENT,
  getActiveProjectProviderId,
  shouldRunOktaSswsSessionSync,
} from "../utils/activeProjectProvider.js";

const AuthContext = createContext(null);

/**
 * App session + SSWS-derived current capabilities (admin roles), validated via server.
 */
export function AuthProvider({ children }) {
  const location = useLocation();
  const { orgUrl: sswsOrgUrl, apiToken: sswsApiToken, clearSswsUnauthorized, stripTokenAfterUnauthorized } =
    useOktaSession();
  const [currentCapabilities, setCurrentCapabilities] = useState(null);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(true);
  /** True while POST validate-ssws is running for stored org+token (localStorage / Connect). */
  const [sswsTokenValidationPending, setSswsTokenValidationPending] = useState(false);
  /** After `/api/idp/okta/capabilities` returns 401 — stop retry loops; UI opens Connect Okta. */
  const [capabilitiesUnauthorized, setCapabilitiesUnauthorized] = useState(false);
  const capabilitiesFetchSuspendedRef = useRef(false);
  const [activeProviderRev, setActiveProviderRev] = useState(0);

  useEffect(() => {
    const onProvider = () => setActiveProviderRev((n) => n + 1);
    window.addEventListener(ACTIVE_PROJECT_PROVIDER_EVENT, onProvider);
    return () => window.removeEventListener(ACTIVE_PROJECT_PROVIDER_EVENT, onProvider);
  }, []);

  const oktaSswsSyncEnabled = useMemo(
    () =>
      shouldRunOktaSswsSessionSync(location.pathname, getActiveProjectProviderId()),
    [location.pathname, activeProviderRev],
  );

  const resetCapabilitiesAuthGate = useCallback(() => {
    capabilitiesFetchSuspendedRef.current = false;
    setCapabilitiesUnauthorized(false);
  }, []);

  const refreshCapabilities = useCallback(async () => {
    if (!oktaSswsSyncEnabled) {
      setCapabilitiesLoading(false);
      return;
    }
    if (capabilitiesFetchSuspendedRef.current) {
      setCapabilitiesLoading(false);
      return;
    }
    setCapabilitiesLoading(true);
    try {
      const stored = readStoredSswsSession();
      const hasStoredToken = Boolean(stored?.apiToken && String(stored.apiToken).trim());
      if (!hasStoredToken) {
        setCurrentCapabilities(null);
        return;
      }
      const res = await fetch("/api/idp/okta/capabilities", { credentials: "include" });
      if (res.status === 401) {
        setCurrentCapabilities(null);
        capabilitiesFetchSuspendedRef.current = true;
        setCapabilitiesUnauthorized(true);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.currentCapabilities?.userId) {
        setCurrentCapabilities(data.currentCapabilities);
      } else {
        setCurrentCapabilities(null);
      }
    } catch {
      setCurrentCapabilities(null);
    } finally {
      setCapabilitiesLoading(false);
    }
  }, [oktaSswsSyncEnabled]);

  /**
   * Validates SSWS token via GET /api/v1/users/me (+ roles). Updates session + Current Capabilities.
   */
  const validateSswsToken = useCallback(async (orgUrl, token) => {
    const res = await fetch("/api/idp/okta/validate-ssws", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ orgUrl, token }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.ok) {
      const err = new Error(data.error || `Validation failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    capabilitiesFetchSuspendedRef.current = false;
    setCapabilitiesUnauthorized(false);
    setCurrentCapabilities(data.currentCapabilities ?? null);
    return data.currentCapabilities;
  }, []);

  /** Re-sync server session when Okta SSWS is present — Okta project routes only (rev5). */
  useEffect(() => {
    if (!oktaSswsSyncEnabled) return undefined;
    if (!sswsOrgUrl?.trim() || !sswsApiToken?.trim()) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await validateSswsToken(sswsOrgUrl.trim(), sswsApiToken.trim());
        if (!cancelled) clearSswsUnauthorized();
      } catch (e) {
        if (cancelled) return;
        if (e?.status === 401) {
          stripTokenAfterUnauthorized();
          setCurrentCapabilities(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    oktaSswsSyncEnabled,
    sswsOrgUrl,
    sswsApiToken,
    validateSswsToken,
    clearSswsUnauthorized,
    stripTokenAfterUnauthorized,
  ]);

  const clearCapabilities = useCallback(async () => {
    try {
      await fetch("/api/idp/okta/capabilities/clear", { method: "POST", credentials: "include" });
    } finally {
      setCurrentCapabilities(null);
      capabilitiesFetchSuspendedRef.current = false;
      setCapabilitiesUnauthorized(false);
    }
  }, []);

  useEffect(() => {
    let hasToken = false;
    try {
      const stored = readStoredSswsSession();
      hasToken = Boolean(stored?.apiToken && String(stored.apiToken).trim());
    } catch {
      hasToken = false;
    }
    if (!hasToken) {
      setCapabilitiesLoading(false);
      setCurrentCapabilities(null);
      return;
    }
    refreshCapabilities();
  }, [refreshCapabilities, location.pathname, activeProviderRev, oktaSswsSyncEnabled]);

  const canCreate =
    currentCapabilities == null ? true : Boolean(currentCapabilities.canCreate !== false);

  const value = useMemo(
    () => ({
      currentCapabilities,
      capabilitiesLoading,
      sswsTokenValidationPending,
      capabilitiesUnauthorized,
      resetCapabilitiesAuthGate,
      refreshCapabilities,
      validateSswsToken,
      clearCapabilities,
      canCreate,
    }),
    [
      currentCapabilities,
      capabilitiesLoading,
      sswsTokenValidationPending,
      capabilitiesUnauthorized,
      resetCapabilitiesAuthGate,
      refreshCapabilities,
      validateSswsToken,
      clearCapabilities,
      canCreate,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
